import {flatten, uniq} from 'underscore';

import {assert} from 'Asserts';
import {StandardFeatureItemState} from 'auth/capabilities/getCapabilityFeatures';
import {Feature, FeatureDefinition} from 'auth/feature-enablement/FeatureConstants';
import {BasicPermissionsGraphQL, Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';

import {StandardFeature} from './CapabilityConstants';

// The Feature type only represents features that are ready to be used in gates. So, any defined but
// in-progress features can be ignored here during development. Right now, the only pre-requisite for use in a
// gate is that the feature has a mapping defined in isCapabilityFeatureEnabledFromBenchlingConfig.
type IgnoredFeatures = never;
export type CapabilityFeature = Exclude<AnyCapabilityFeature, IgnoredFeatures>;

export enum Capability {
  ADVANCED_ANALYSIS = 'ADVANCED_ANALYSIS',
  ASSAYS = 'ASSAYS',
  BIOLOGICS = 'BIOLOGICS',
  DEVELOPER_PLATFORM = 'DEVELOPER_PLATFORM',
  INSIGHTS = 'INSIGHTS',
  INVENTORY = 'INVENTORY',
  LAB_AUTOMATION = 'LAB_AUTOMATION',
  MOLBIO = 'MOLBIO',
  NOTEBOOK = 'NOTEBOOK',
  PROCESSES = 'PROCESSES',
  REGISTRY = 'REGISTRY',
  REQUESTS = 'REQUESTS',
  RESULTS = 'RESULTS',
  RECIPES = 'RECIPES',
  V2_REQUESTS = 'V2_REQUESTS',
  WORKFLOWS = 'WORKFLOWS',
}
export const UNIMPLEMENTED_CAPABILITIES = [Capability.NOTEBOOK] as const;
export const USER_PREFERENCE_GATED_CAPABILITIES = [Capability.REQUESTS, Capability.WORKFLOWS] as const;
export const IMPLEMENTED_CAPABILITIES = Object.keys(Capability).filter(
  (capability) =>
    // oxlint-disable-next-line @typescript-eslint/no-explicit-any
    !UNIMPLEMENTED_CAPABILITIES.includes(capability as any) &&
    // oxlint-disable-next-line @typescript-eslint/no-explicit-any
    !USER_PREFERENCE_GATED_CAPABILITIES.includes(capability as any)
) as Array<Capability>;
export type UnimplementedCapability = (typeof UNIMPLEMENTED_CAPABILITIES)[number];
export type UserPreferenceGatedCapability = (typeof USER_PREFERENCE_GATED_CAPABILITIES)[number];
export type ImplementedCapability = Exclude<
  Capability,
  UnimplementedCapability | UserPreferenceGatedCapability
>;
// Keep in sync with benchling/auth/capabilities/constants.py
export const CAPABILITIES_WITHOUT_READ_ACCESS = [
  ...USER_PREFERENCE_GATED_CAPABILITIES,
  Capability.LAB_AUTOMATION,
  Capability.DEVELOPER_PLATFORM,
];

export const CAPABILITY_ONBOARDING_ORDER: Array<Capability> = [
  Capability.NOTEBOOK,
  Capability.MOLBIO,
  Capability.REGISTRY,
  Capability.BIOLOGICS,
  Capability.RESULTS,
  Capability.INVENTORY,
  Capability.REQUESTS,
  Capability.WORKFLOWS,
  Capability.LAB_AUTOMATION,
  Capability.INSIGHTS,
  Capability.ADVANCED_ANALYSIS,
  Capability.DEVELOPER_PLATFORM,
  Capability.RECIPES,
  Capability.ASSAYS,
  Capability.V2_REQUESTS,
];

export const CAPABILITY_TO_LEGACY_FLAG: {
  [key in ImplementedCapability | UserPreferenceGatedCapability]: null | keyof typeof BenchlingConfig;
} = {
  [Capability.ADVANCED_ANALYSIS]: null,
  [Capability.ASSAYS]: null,
  [Capability.BIOLOGICS]: 'MOLBIO_ENABLE_ANTIBODY_ENTITIES',
  [Capability.INSIGHTS]: 'ENABLE_EXTERNAL_ANALYTICS_UI',
  [Capability.REGISTRY]: 'ENABLE_REGISTRY',
  [Capability.INVENTORY]: 'ENABLE_STORAGE',
  [Capability.WORKFLOWS]: 'LEGACY_WORKFLOWS_ENABLE',
  [Capability.REQUESTS]: 'ENABLE_REQUESTS',
  [Capability.RESULTS]: 'ENABLE_NEW_RESULTS',
  [Capability.LAB_AUTOMATION]: 'ENABLE_LAB_AUTOMATION',
  [Capability.MOLBIO]: null,
  [Capability.DEVELOPER_PLATFORM]: null,
  [Capability.PROCESSES]: 'WORKFLOWS_ENABLE',
  [Capability.RECIPES]: null,
  [Capability.V2_REQUESTS]: null,
} as const;

export enum AdvancedAnalysisFeature {
  ADVANCED_ANALYSIS_CREATE_STATISTICS = 'ADVANCED_ANALYSIS_CREATE_STATISTICS',
  ADVANCED_ANALYSIS_CREATE_TEMPLATES = 'ADVANCED_ANALYSIS_CREATE_TEMPLATES',
}
export enum InsightsFeature {
  INSIGHTS_CREATE = 'INSIGHTS_CREATE',
  INSIGHTS_DISCOVER = 'INSIGHTS_DISCOVER',
  INSIGHTS_DELETE = 'INSIGHTS_DELETE',
  INSIGHTS_EDIT = 'INSIGHTS_EDIT',
  INSIGHTS_MOVE = 'INSIGHTS_MOVE',
  INSIGHTS_OPEN = 'INSIGHTS_OPEN',
}
export enum InventoryFeature {
  INVENTORY_ADMIN = 'INVENTORY_ADMIN',
  INVENTORY_DISCOVER = 'INVENTORY_DISCOVER',
  INVENTORY_IMPORT = 'INVENTORY_IMPORT',
  INVENTORY_EXPORT = 'INVENTORY_EXPORT',
  INVENTORY_CREATE = 'INVENTORY_CREATE',
  INVENTORY_DELETE = 'INVENTORY_DELETE',
  INVENTORY_EDIT = 'INVENTORY_EDIT',
  INVENTORY_MOVE = 'INVENTORY_MOVE',
  INVENTORY_OPEN = 'INVENTORY_OPEN',
  INVENTORY_SHOW_DETAILS = 'INVENTORY_SHOW_DETAILS',
  INVENTORY_AT_MENTION = 'INVENTORY_AT_MENTION',
  INVENTORY_ARCHIVE = 'INVENTORY_ARCHIVE',
  INVENTORY_UNARCHIVE = 'INVENTORY_UNARCHIVE',
}
export enum LabAutomationFeature {
  LAB_AUTOMATION_ADMIN = 'LAB_AUTOMATION_ADMIN',
  LAB_AUTOMATION_ARCHIVE = 'LAB_AUTOMATION_ARCHIVE',
  LAB_AUTOMATION_CREATE = 'LAB_AUTOMATION_CREATE',
  LAB_AUTOMATION_DELETE = 'LAB_AUTOMATION_DELETE',
  LAB_AUTOMATION_DISCOVER = 'LAB_AUTOMATION_DISCOVER',
  LAB_AUTOMATION_EDIT = 'LAB_AUTOMATION_EDIT',
  LAB_AUTOMATION_EDIT_TAGS = 'LAB_AUTOMATION_EDIT_TAGS',
  LAB_AUTOMATION_IMPORT = 'LAB_AUTOMATION_IMPORT',
  LAB_AUTOMATION_OPEN = 'LAB_AUTOMATION_OPEN',
  LAB_AUTOMATION_UNARCHIVE = 'LAB_AUTOMATION_UNARCHIVE',
}
export enum BiologicsFeature {
  BIOLOGICS_ADMIN = 'BIOLOGICS_ADMIN',
  BIOLOGICS_IMPORT = 'BIOLOGICS_IMPORT',
  BIOLOGICS_EXPORT = 'BIOLOGICS_EXPORT',
  BIOLOGICS_CREATE = 'BIOLOGICS_CREATE',
  BIOLOGICS_DELETE = 'BIOLOGICS_DELETE',
  BIOLOGICS_EDIT = 'BIOLOGICS_EDIT',
  BIOLOGICS_MOVE = 'BIOLOGICS_MOVE',
  BIOLOGICS_OPEN = 'BIOLOGICS_OPEN',
  BIOLOGICS_ARCHIVE = 'BIOLOGICS_ARCHIVE',
  BIOLOGICS_UNARCHIVE = 'BIOLOGICS_UNARCHIVE',
}

export enum MolbioFeature {
  MOLBIO_ADMIN = 'MOLBIO_ADMIN',
  MOLBIO_IMPORT = 'MOLBIO_IMPORT',
  MOLBIO_EXPORT = 'MOLBIO_EXPORT',
  MOLBIO_CREATE = 'MOLBIO_CREATE',
  MOLBIO_DELETE = 'MOLBIO_DELETE',
  MOLBIO_EDIT = 'MOLBIO_EDIT',
  MOLBIO_MOVE = 'MOLBIO_MOVE',
  MOLBIO_OPEN = 'MOLBIO_OPEN',
  MOLBIO_ARCHIVE = 'MOLBIO_ARCHIVE',
  MOLBIO_UNARCHIVE = 'MOLBIO_UNARCHIVE',
  MOLBIO_ADD_TO_WORKLIST = 'MOLBIO_ADD_TO_WORKLIST',
}
export enum ProcessesFeature {
  PROCESSES_ADMIN = 'PROCESSES_ADMIN',
  PROCESSES_DISCOVER = 'PROCESSES_DISCOVER',
  PROCESSES_EXPORT = 'PROCESSES_EXPORT',
  PROCESSES_CREATE = 'PROCESSES_CREATE',
  PROCESSES_DELETE = 'PROCESSES_DELETE',
  PROCESSES_EDIT = 'PROCESSES_EDIT',
  PROCESSES_EDIT_TAGS = 'PROCESSES_EDIT_TAGS',
  PROCESSES_MOVE = 'PROCESSES_MOVE',
  PROCESSES_OPEN = 'PROCESSES_OPEN',
  PROCESSES_SHOW_DETAILS = 'PROCESSES_SHOW_DETAILS',
  PROCESSES_AT_MENTION = 'PROCESSES_AT_MENTION',
  PROCESSES_ARCHIVE = 'PROCESSES_ARCHIVE',
  PROCESSES_UNARCHIVE = 'PROCESSES_UNARCHIVE',
}
export enum RegistryFeature {
  REGISTRY_ADMIN = 'REGISTRY_ADMIN',
  REGISTRY_DISCOVER = 'REGISTRY_DISCOVER',
  REGISTRY_IMPORT = 'REGISTRY_IMPORT',
  REGISTRY_EXPORT = 'REGISTRY_EXPORT',
  REGISTRY_CREATE = 'REGISTRY_CREATE',
  REGISTRY_DELETE = 'REGISTRY_DELETE',
  REGISTRY_EDIT = 'REGISTRY_EDIT',
  REGISTRY_EDIT_TAGS = 'REGISTRY_EDIT_TAGS',
  REGISTRY_MOVE = 'REGISTRY_MOVE',
  REGISTRY_OPEN = 'REGISTRY_OPEN',
  REGISTRY_SHOW_DETAILS = 'REGISTRY_SHOW_DETAILS',
  REGISTRY_AT_MENTION = 'REGISTRY_AT_MENTION',
  REGISTRY_ARCHIVE = 'REGISTRY_ARCHIVE',
  REGISTRY_UNARCHIVE = 'REGISTRY_UNARCHIVE',
  REGISTRY_SET_SCHEMA = 'REGISTRY_SET_SCHEMA',
  REGISTRY_REGISTER = 'REGISTRY_REGISTER',
  REGISTRY_UNREGISTER = 'REGISTRY_UNREGISTER',
  REGISTRY_BATCHES = 'REGISTRY_BATCHES',
  REGISTRY_MIXTURES = 'REGISTRY_MIXTURES',
  REGISTRY_WORKLISTS = 'REGISTRY_WORKLISTS',
}
export enum ResultsFeature {
  RESULTS_ADMIN = 'RESULTS_ADMIN',
  RESULTS_CREATE = 'RESULTS_CREATE',
  RESULTS_OPEN = 'RESULTS_OPEN',
  RESULTS_SHOW_DETAILS = 'RESULTS_SHOW_DETAILS',
}
export enum DeveloperPlatformFeature {
  DEVELOPER_PLATFORM_DISCOVER = 'DEVELOPER_PLATFORM_DISCOVER',
  DEVELOPER_PLATFORM_API_KEY_AUTH = 'DEVELOPER_PLATFORM_API_KEY_AUTH',
  DEVELOPER_PLATFORM_ADMIN = 'DEVELOPER_PLATFORM_ADMIN',
  DEVELOPER_PLATFORM_OPEN = 'DEVELOPER_PLATFORM_OPEN',
}

export enum RecipesFeature {
  RECIPES_ADMIN = 'RECIPES_ADMIN',
  RECIPES_ARCHIVE = 'RECIPES_ARCHIVE',
  RECIPES_AT_MENTION = 'RECIPES_AT_MENTION',
  RECIPES_CREATE_ANALYSIS = 'RECIPES_CREATE_ANALYSIS',
  RECIPES_CREATE_RECIPE = 'RECIPES_CREATE_RECIPE',
  RECIPES_CREATE_STUDY = 'RECIPES_CREATE_STUDY',
  RECIPES_DISCOVER = 'RECIPES_DISCOVER',
  RECIPES_EDIT = 'RECIPES_EDIT',
  RECIPES_EDIT_CONFIG = 'RECIPES_EDIT_CONFIG',
  RECIPES_EXECUTE_EXPERIMENT = 'RECIPES_EXECUTE_EXPERIMENT',
  RECIPES_EXPORT = 'RECIPES_EXPORT',
  RECIPES_MOVE = 'RECIPES_MOVE',
  RECIPES_OPEN = 'RECIPES_OPEN',
  RECIPES_PLAN_EXPERIMENT = 'RECIPES_PLAN_EXPERIMENT',
  RECIPES_SHOW_DETAILS = 'RECIPES_SHOW_DETAILS',
  RECIPES_UNARCHIVE = 'RECIPES_UNARCHIVE',
}

export enum AssaysFeature {
  ASSAYS_ADMIN = 'ASSAYS_ADMIN',
  ASSAYS_ARCHIVE = 'ASSAYS_ARCHIVE',
  ASSAYS_AT_MENTION = 'ASSAYS_AT_MENTION',
  ASSAYS_CREATE_ANALYSIS = 'ASSAYS_CREATE_ANALYSIS',
  ASSAYS_CREATE_ASSAY = 'ASSAYS_CREATE_ASSAY',
  ASSAYS_CREATE_STUDY = 'ASSAYS_CREATE_STUDY',
  ASSAYS_DISCOVER = 'ASSAYS_DISCOVER',
  ASSAYS_EDIT_CONFIG = 'ASSAYS_EDIT_CONFIG',
  ASSAYS_EXECUTE_EXPERIMENT = 'ASSAYS_EXECUTE_EXPERIMENT',
  ASSAYS_EXPORT = 'ASSAYS_EXPORT',
  ASSAYS_MOVE = 'ASSAYS_MOVE',
  ASSAYS_OPEN = 'ASSAYS_OPEN',
  ASSAYS_PLAN_EXPERIMENT = 'ASSAYS_PLAN_EXPERIMENT',
  ASSAYS_SHOW_DETAILS = 'ASSAYS_SHOW_DETAILS',
  ASSAYS_UNARCHIVE = 'ASSAYS_UNARCHIVE',
}

export enum V2RequestsFeature {
  V2_REQUESTS_ADMIN = 'V2_REQUESTS_ADMIN',
  V2_REQUESTS_DISCOVER = 'V2_REQUESTS_DISCOVER',
  V2_REQUESTS_EXPORT = 'V2_REQUESTS_EXPORT',
  V2_REQUESTS_CREATE = 'V2_REQUESTS_CREATE',
  V2_REQUESTS_DELETE = 'V2_REQUESTS_DELETE',
  V2_REQUESTS_EDIT = 'V2_REQUESTS_EDIT',
  V2_REQUESTS_MOVE = 'V2_REQUESTS_MOVE',
  V2_REQUESTS_OPEN = 'V2_REQUESTS_OPEN',
  V2_REQUESTS_SHOW_DETAILS = 'V2_REQUESTS_SHOW_DETAILS',
  V2_REQUESTS_AT_MENTION = 'V2_REQUESTS_AT_MENTION',
  V2_REQUESTS_ARCHIVE = 'V2_REQUESTS_ARCHIVE',
  V2_REQUESTS_UNARCHIVE = 'V2_REQUESTS_UNARCHIVE',
}

export type AnyCapabilityFeature =
  | AdvancedAnalysisFeature
  | AssaysFeature
  | BiologicsFeature
  | InsightsFeature
  | InventoryFeature
  | LabAutomationFeature
  | MolbioFeature
  | RegistryFeature
  | ResultsFeature
  | DeveloperPlatformFeature
  | ProcessesFeature
  | RecipesFeature
  | V2RequestsFeature;

export type FeatureType =
  | typeof AdvancedAnalysisFeature
  | typeof AssaysFeature
  | typeof BiologicsFeature
  | typeof InsightsFeature
  | typeof InventoryFeature
  | typeof LabAutomationFeature
  | typeof MolbioFeature
  | typeof RegistryFeature
  | typeof ResultsFeature
  | typeof DeveloperPlatformFeature
  | typeof ProcessesFeature
  | typeof RecipesFeature
  | typeof V2RequestsFeature;

// Used for tests
export const _featureLists = [
  AdvancedAnalysisFeature,
  AssaysFeature,
  BiologicsFeature,
  InsightsFeature,
  InventoryFeature,
  LabAutomationFeature,
  MolbioFeature,
  RegistryFeature,
  ResultsFeature,
  DeveloperPlatformFeature,
  ProcessesFeature,
  RecipesFeature,
  V2RequestsFeature,
];

// This noop function is to get type safety without clobbering the inferred type because
// FeatureWithPermissionMapping needs a list of just the keys in FEATURE_TO_PERMISSION_KEY, not all features.
// If FEATURE_TO_PERMISSION_KEY were defined with a type like {[K in FeatureKeys]?: keyof Permissions},
// the type generated with `keyof typeof FEATURE_TO_PERMISSION_KEY` would include all Features, not just those
// in the mapping.
// oxlint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeTypesafe = <T extends Feature>(_: {[K in T]: keyof Permissions}) => _;

export const FEATURE_TO_PERMISSION_KEY = makeTypesafe({
  [BiologicsFeature.BIOLOGICS_ARCHIVE]: 'archivable',
  [BiologicsFeature.BIOLOGICS_DELETE]: 'deletable',
  [BiologicsFeature.BIOLOGICS_EDIT]: 'writable',
  [BiologicsFeature.BIOLOGICS_EXPORT]: 'writable',
  [BiologicsFeature.BIOLOGICS_MOVE]: 'movable',
  [BiologicsFeature.BIOLOGICS_UNARCHIVE]: 'unarchivable',
  // Maintain a list of permissions that need to be mapped once Inventory is supported
  [InventoryFeature.INVENTORY_ARCHIVE]: 'archivable',
  [InventoryFeature.INVENTORY_DELETE]: 'deletable',
  [InventoryFeature.INVENTORY_EDIT]: 'writable',
  [InventoryFeature.INVENTORY_EXPORT]: 'writable',
  [InventoryFeature.INVENTORY_MOVE]: 'movable',
  [InventoryFeature.INVENTORY_UNARCHIVE]: 'unarchivable',
  [LabAutomationFeature.LAB_AUTOMATION_ARCHIVE]: 'archivable',
  [LabAutomationFeature.LAB_AUTOMATION_UNARCHIVE]: 'unarchivable',
  [MolbioFeature.MOLBIO_ARCHIVE]: 'archivable',
  [MolbioFeature.MOLBIO_DELETE]: 'deletable',
  [MolbioFeature.MOLBIO_EDIT]: 'writable',
  [MolbioFeature.MOLBIO_MOVE]: 'movable',
  [MolbioFeature.MOLBIO_UNARCHIVE]: 'unarchivable',
  [RegistryFeature.REGISTRY_ARCHIVE]: 'archivable',
  [RegistryFeature.REGISTRY_DELETE]: 'deletable',
  [RegistryFeature.REGISTRY_EDIT]: 'writable',
  [RegistryFeature.REGISTRY_MOVE]: 'movable',
  [RegistryFeature.REGISTRY_UNREGISTER]: 'unregisterable',
  [RegistryFeature.REGISTRY_REGISTER]: 'writable',
  [RegistryFeature.REGISTRY_UNARCHIVE]: 'unarchivable',
  [ProcessesFeature.PROCESSES_ARCHIVE]: 'archivable',
  [ProcessesFeature.PROCESSES_DELETE]: 'deletable',
  [ProcessesFeature.PROCESSES_EDIT]: 'writable',
  [ProcessesFeature.PROCESSES_MOVE]: 'movable',
  [ProcessesFeature.PROCESSES_UNARCHIVE]: 'unarchivable',
});

export const INTENTIONALLY_SKIPPED_FEATURE = 'INTENTIONALLY_SKIPPED_FEATURE';

// Maps standard features to permission keys to check a user's permissions if a StandardFeature cannot
// be mapped to a capability feature. This list is derived from the FEATURE_TO_PERMISSION_KEY list as it
// should always reflect the permissions check that was replaced by the capability management gate. For
// gates that use a StandardFeature and a ItemWithPermissions but did not originally check permissions, we
// use INTENTIONALLY_SKIPPED_FEATURE to signal that the permissions check should be skipped.
export const UNMAPPED_STANDARD_FEATURE_TO_PERMISSION_KEY = {
  [StandardFeature.OPEN]: INTENTIONALLY_SKIPPED_FEATURE,
  [StandardFeature.SHOW_DETAILS]: INTENTIONALLY_SKIPPED_FEATURE,
  [StandardFeature.CREATE]: INTENTIONALLY_SKIPPED_FEATURE,
  [StandardFeature.EXPORT]: INTENTIONALLY_SKIPPED_FEATURE,
  [StandardFeature.ARCHIVE]: 'archivable',
  [StandardFeature.DELETE]: 'deletable',
  [StandardFeature.EDIT]: 'writable',
  [StandardFeature.EDIT_TAGS]: 'tagsEditable',
  [StandardFeature.MOVE]: 'movable',
  [StandardFeature.UNARCHIVE]: 'unarchivable',
} as const;

export type PermissionsLookupSupportedStandardFeatures =
  keyof typeof UNMAPPED_STANDARD_FEATURE_TO_PERMISSION_KEY;

export type FeatureWithPermissionMapping = keyof typeof FEATURE_TO_PERMISSION_KEY;
export type FeatureWithoutPermissionsMapping = Exclude<CapabilityFeature, FeatureWithPermissionMapping>;

export const CAPABILITY_TO_NAME: {[key in Capability]: string} = {
  ADVANCED_ANALYSIS: 'Advanced Analysis',
  ASSAYS: 'Assays',
  BIOLOGICS: 'Biologics',
  INSIGHTS: 'Insights',
  INVENTORY: 'Inventory',
  LAB_AUTOMATION: 'Lab Automation',
  MOLBIO: 'Molecular Biology',
  NOTEBOOK: 'Notebook',
  REGISTRY: 'Registry',
  REQUESTS: 'Requests',
  RESULTS: 'Results',
  WORKFLOWS: 'Workflows',
  DEVELOPER_PLATFORM: 'Developer Platform',
  PROCESSES: 'Workflows',
  RECIPES: 'Recipes',
  V2_REQUESTS: 'Requests',
};
export type ApplicationName = (typeof CAPABILITY_TO_NAME)[keyof typeof CAPABILITY_TO_NAME];

export const CAPABILITY_TO_FEATURE_TYPE: {
  [key in ImplementedCapability]: FeatureType;
} = {
  ADVANCED_ANALYSIS: AdvancedAnalysisFeature,
  ASSAYS: AssaysFeature,
  BIOLOGICS: BiologicsFeature,
  INSIGHTS: InsightsFeature,
  INVENTORY: InventoryFeature,
  LAB_AUTOMATION: LabAutomationFeature,
  MOLBIO: MolbioFeature,
  REGISTRY: RegistryFeature,
  RESULTS: ResultsFeature,
  DEVELOPER_PLATFORM: DeveloperPlatformFeature,
  PROCESSES: ProcessesFeature,
  RECIPES: RecipesFeature,
  V2_REQUESTS: V2RequestsFeature,
};

export const MODEL_TYPE_TO_CAPABILITIES = {
  [ModelTypeConstants.ANALYTICS_DASHBOARD]: [Capability.INSIGHTS],
  [ModelTypeConstants.ASSAY_RUN]: [Capability.LAB_AUTOMATION],
  [ModelTypeConstants.ANTIBODY]: [Capability.BIOLOGICS, Capability.REGISTRY],
  [ModelTypeConstants.BASIC_FOLDER_ITEM]: [Capability.REGISTRY],
  [ModelTypeConstants.BULK_ASSEMBLY]: [Capability.MOLBIO],
  [ModelTypeConstants.CONNECTION]: [Capability.LAB_AUTOMATION],
  [ModelTypeConstants.MIXTURE]: [Capability.REGISTRY],
  [ModelTypeConstants.SEQUENCE]: [Capability.MOLBIO, Capability.REGISTRY],
  [ModelTypeConstants.PROTEIN]: [Capability.MOLBIO, Capability.REGISTRY],
  [ModelTypeConstants.DNA_OLIGO]: [Capability.MOLBIO, Capability.REGISTRY],
  [ModelTypeConstants.BATCH]: [Capability.REGISTRY],
  [ModelTypeConstants.SEQUENCE_ANALYSIS]: [Capability.MOLBIO],
  [ModelTypeConstants.PROTEIN_ALIGNMENT]: [Capability.MOLBIO],
  [ModelTypeConstants.BOX]: [Capability.INVENTORY],
  [ModelTypeConstants.CONTAINER]: [Capability.INVENTORY],
  [ModelTypeConstants.LOCATION]: [Capability.INVENTORY],
  [ModelTypeConstants.PLATE]: [Capability.INVENTORY],
  [ModelTypeConstants.PROCESS]: [Capability.PROCESSES],
  [ModelTypeConstants.PROCESS_OUTPUT]: [Capability.PROCESSES],
  [ModelTypeConstants.PROCESS_TASK]: [Capability.PROCESSES],
  [ModelTypeConstants.REQUEST_V2_SUBMISSION]: [Capability.V2_REQUESTS],
  [ModelTypeConstants.REQUEST_V2_DEFINITION]: [Capability.V2_REQUESTS],
} as const;
type ModelTypeToCapabilityMap = typeof MODEL_TYPE_TO_CAPABILITIES;
type StandardFeatureMappedCapabilities = ModelTypeToCapabilityMap[keyof ModelTypeToCapabilityMap][number];
export type MappedToCapabilityModelType = keyof ModelTypeToCapabilityMap;

function getMappedCapabilities(): Array<StandardFeatureMappedCapabilities> {
  return uniq(flatten(Object.values(MODEL_TYPE_TO_CAPABILITIES)));
}

export const MAPPED_CAPABILITIES = getMappedCapabilities();

// Unused StandardFeatures must be added here for each capability mapped to with either
// MODEL_TYPE_TO_CAPABILITIES. Before adding a StandardFeature to the
// capability's unused list, it is important to verify that all uses of the StandardFeature will never be used
// in code that maps to the capability. E.g. nowhere in the UI can you archive an Insights dashboard including
// shared UI like the expanded listing toolbar. So adding StandardFeature.ARCHIVE to the Insights' unused list
// is safe.
export const CAPABILITY_TO_UNUSED_STANDARD_FEATURES: {
  [key in StandardFeatureMappedCapabilities]: Array<StandardFeature>;
} = {
  INSIGHTS: [
    StandardFeature.ADMIN,
    StandardFeature.IMPORT,
    StandardFeature.EXPORT,
    StandardFeature.SHOW_DETAILS,
    StandardFeature.AT_MENTION,
    StandardFeature.ARCHIVE,
    StandardFeature.UNARCHIVE,
    StandardFeature.EDIT_TAGS,
  ],
  BIOLOGICS: [
    StandardFeature.DISCOVER,
    StandardFeature.SHOW_DETAILS,
    StandardFeature.AT_MENTION,
    // Tag edits are covered by BIOLOGICS_EDIT rather than a separate BIOLOGICS_EDIT_TAGS feature.
    StandardFeature.EDIT_TAGS,
  ],
  INVENTORY: [
    // While tags can be edited on inventory items, we rely on the EDIT feature to determine the capability.
    StandardFeature.EDIT_TAGS,
  ],
  MOLBIO: [
    StandardFeature.DISCOVER,
    StandardFeature.SHOW_DETAILS,
    StandardFeature.AT_MENTION,
    // While tags can be edited on molbio items, we rely on the EDIT feature to determine the capability.
    StandardFeature.EDIT_TAGS,
  ],
  LAB_AUTOMATION: [
    StandardFeature.IMPORT,
    StandardFeature.EXPORT,
    StandardFeature.SHOW_DETAILS,
    StandardFeature.AT_MENTION,
    StandardFeature.MOVE,
  ],
  REGISTRY: [],
  PROCESSES: [StandardFeature.IMPORT],
  V2_REQUESTS: [StandardFeature.IMPORT, StandardFeature.EDIT_TAGS],
};

MAPPED_CAPABILITIES.forEach((capability) => {
  const unusedStandardFeatures = CAPABILITY_TO_UNUSED_STANDARD_FEATURES[capability];
  const capabilityFeatures = Object.keys(CAPABILITY_TO_FEATURE_TYPE[capability]);
  Object.keys(StandardFeature).forEach((standardFeature) => {
    assert(
      capabilityFeatures.includes(`${capability}_${standardFeature}`) ||
        unusedStandardFeatures.includes(standardFeature as StandardFeature),
      `The feature list for ${CAPABILITY_TO_NAME[capability]} must include all StandardFeatures not` +
        ` ignored with CAPABILITY_TO_UNUSED_STANDARD_FEATURES. Missing: ${standardFeature}`
    );
  });
});

export interface ItemWithPermissions {
  id: string;
  permissions: Permissions | BasicPermissionsGraphQL;
}

export interface BasicItem {
  id: string;
  permissions?: Permissions | BasicPermissionsGraphQL;
}

export interface FeatureWithoutPermissionsMappingDefinition {
  feature: FeatureWithoutPermissionsMapping;
  item?: BasicItem;
}

export interface FeatureWithPermissionsMappingDefinition {
  feature: FeatureWithPermissionMapping;
  item?: ItemWithPermissions;
}

export type CapabilityFeatureDefinition =
  | FeatureWithPermissionsMappingDefinition
  | FeatureWithoutPermissionsMappingDefinition;

export interface StandardFeatureDefinitionWithItem {
  feature: StandardFeature;
  item: BasicItem;
  // Optional per-item state for capability routing (isRegistered, isBiologicsOwned, etc.). Callers derive
  // this locally or via helpers like standardFeatureItemStateFromListableItem; see StandardFeatureItemState
  // in getCapabilityFeatures.ts for the derive / gate / enforce layering.
  itemState?: StandardFeatureItemState;
}

interface StandardFeatureDefinitionWithItemType {
  feature: StandardFeature;
  itemType: MappedToCapabilityModelType;
}

export type StandardFeatureDefinition =
  | StandardFeatureDefinitionWithItem
  | StandardFeatureDefinitionWithItemType;

export function isFeatureWithPermissionMapping(
  feature: Feature | StandardFeature
): feature is FeatureWithPermissionMapping {
  return Object.keys(FEATURE_TO_PERMISSION_KEY).includes(feature);
}

export function isFeatureWithPermissionMappingDefinition(
  featureDefinition: FeatureDefinition
): featureDefinition is FeatureWithPermissionsMappingDefinition {
  return isFeatureWithPermissionMapping(featureDefinition.feature);
}

export function isStandardFeatureDefinition(
  featureDefinition: FeatureDefinition
): featureDefinition is StandardFeatureDefinition {
  return Object.keys(StandardFeature).includes(featureDefinition.feature);
}

export function isStandardFeatureDefinitionWithItemType(
  featureDefinition: StandardFeatureDefinition
): featureDefinition is StandardFeatureDefinitionWithItemType {
  return Boolean((featureDefinition as StandardFeatureDefinitionWithItemType).itemType);
}

export function isStandardFeatureDefinitionWithItem(
  featureDefinition: StandardFeatureDefinition
): featureDefinition is StandardFeatureDefinitionWithItem {
  return Boolean((featureDefinition as StandardFeatureDefinitionWithItem).item);
}

export function isItemWithPermissions(item: BasicItem): item is ItemWithPermissions {
  return Boolean((item as ItemWithPermissions).permissions);
}
